import React, { useEffect, useState } from "react";
import { useWorkerCreation } from "@/context/WorkerCreationContext";
import { WorkerAniAPI } from "@/services/api";
import type { WorkerTool } from "@/services/api/worker-ani/types";
import { Button } from "@/components/ui/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faPlus,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { DropDown } from "@/components/ui/dropdown";
import CapabilityEditDialog from "./CapabilityEditDialog";
import { Badge } from "@/components/ui/badge/Badge";

const CapabilityCard = ({
  capability,
  selected,
  onToggle,
  onEdit,
  onDelete,
  onDuplicate,
}: {
  capability: WorkerTool;
  selected: boolean;
  onToggle: (id: string) => void;
  onEdit: (capability: WorkerTool) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}) => {
  const menuItems =
    capability.tool_scope === "system"
      ? [{ name: "Duplicate", onClick: () => onDuplicate(capability.id) }]
      : [
          { name: "Edit", onClick: () => onEdit(capability) },
          { name: "Delete", onClick: () => onDelete(capability.id) },
        ];

  return (
    <div
      className={`p-4 rounded-lg border-2 transition-all relative group ${
        selected
          ? "border-indigo-500 dark:bg-gradient-to-tl dark:from-indigo-800/20 dark:to-indigo-900/0"
          : "border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500"
      }`}
    >
      <div onClick={() => onToggle(capability.id)} className="cursor-pointer">
        <div className="flex justify-between items-start">
          <div className="font-medium flex flex-grow items-center gap-2 text-slate-800 dark:text-slate-200 ">
            <div className="flex-grow w-0 min-w-0  line-clamp-2">
              {capability.display_name}
            </div>
            <div className="flex-shrink-0 justify-end flex gap-2 items-center">
              <div className=" z-10" onClick={(e) => e.stopPropagation()}>
                <DropDown
                  label={
                    <button className="p-2  text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
                      <FontAwesomeIcon
                        icon={faEllipsisVertical}
                        className="h-5 w-2"
                      />
                    </button>
                  }
                  placement="bottom"
                  className="w-40"
                >
                  {menuItems.map((item) => (
                    <div
                      key={item.name}
                      onClick={item.onClick}
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 focus:outline-none dark:text-slate-200 dark:hover:text-white whitespace-nowrap cursor-pointer"
                    >
                      {item.name}
                    </div>
                  ))}
                </DropDown>
              </div>
            </div>
          </div>
        </div>
        <p className="text-sm mb-4 text-slate-500 dark:text-slate-400 mt-2">
          {capability.description}
        </p>
        <Badge
          color={capability.tool_scope === "system" ? "sky" : "green"}
          className="capitalize"
        >
          {capability.tool_scope}
        </Badge>
      </div>
    </div>
  );
};

export default function CreateWorkerCapabilities() {
  const { workerData, updateWorkerData, selectedTemplate } =
    useWorkerCreation();
  const [capabilities, setCapabilities] = useState<WorkerTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCapability, setEditingCapability] = useState<WorkerTool | null>(
    null
  );
  const [duplicatingCapability, setDuplicatingCapability] =
    useState<WorkerTool | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  useEffect(() => {
    const fetchCapabilities = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await WorkerAniAPI.getWorkerTools();
        setCapabilities(data);
      } catch (err) {
        console.error("Failed to fetch capabilities:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load capabilities"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCapabilities();
  }, []);

  // Pre-select capabilities based on template's default_capabilities
  useEffect(() => {
    if (selectedTemplate?.default_capabilities && capabilities.length > 0) {
      // Only pre-select if no capabilities are currently selected (to avoid overwriting user selections)
      if (workerData.selectedCapabilities.size === 0) {
        const availableCapabilityIds = new Set(
          capabilities.map((cap) => cap.id)
        );
        const validDefaultCapabilities =
          selectedTemplate.default_capabilities.filter((capId: string) =>
            availableCapabilityIds.has(capId)
          );

        if (validDefaultCapabilities.length > 0) {
          updateWorkerData({
            selectedCapabilities: new Set(validDefaultCapabilities),
          });
        }
      }
    }
  }, [
    selectedTemplate,
    capabilities,
    workerData.selectedCapabilities.size,
    updateWorkerData,
  ]);

  const handleDuplicateCapability = async (toolId: string) => {
    const capability = capabilities.find((c) => c.id === toolId);
    if (!capability) return;

    const duplicatedCapability: WorkerTool = {
      ...capability,
      _id: { $oid: `new-${Date.now()}` },
      id: `new-${Date.now()}`,
      display_name: `${capability.display_name} (Copy)`,
    };
    setDuplicatingCapability(duplicatedCapability);
    setIsEditDialogOpen(true);
  };

  const handleSaveDuplicate = async (editedTool: WorkerTool) => {
    if (!duplicatingCapability) return;
    try {
      const payload = {
        display_name: editedTool.display_name,
        description: editedTool.description,
        rules: editedTool.rules,
        tags: editedTool.tags,
        is_active: editedTool.is_active,
      };
      const newTool = await WorkerAniAPI.cloneWorkerTool(
        duplicatingCapability.id,
        payload
      );
      setCapabilities((prev) => [...prev, newTool]);
      setDuplicatingCapability(null);
      setIsEditDialogOpen(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to duplicate capability"
      );
    }
  };

  const toggleCapability = (id: string) => {
    const newSet = new Set(workerData.selectedCapabilities);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    updateWorkerData({ selectedCapabilities: newSet });
  };

  const handleCreateCapability = () => {
    const newCapability: WorkerTool = {
      _id: { $oid: `new-${Date.now()}` },
      id: `new-${Date.now()}`,
      display_name: "New Capability",
      description: "",
      tool_type: "custom",
      rules: [],
      type: "atomic",
      version: "1.0.0",
      is_active: true,
      tags: [],
      tool_scope: "user",
      parent_tool_id: null,
      owner_id: null,
      client_id: null,
      created_at: { $date: new Date().toISOString() },
      updated_at: { $date: new Date().toISOString() },
    };
    setEditingCapability(newCapability);
    setIsEditDialogOpen(true);
  };

  const handleEditCapability = (capability: WorkerTool) => {
    setEditingCapability(capability);
    setIsEditDialogOpen(true);
  };

  const handleDeleteCapability = async (id: string) => {
    try {
      await WorkerAniAPI.deleteWorkerTool(id);
      setCapabilities((prev) => prev.filter((c) => c.id !== id));
      const newSet = new Set(workerData.selectedCapabilities);
      if (newSet.has(id)) {
        newSet.delete(id);
        updateWorkerData({ selectedCapabilities: newSet });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete capability"
      );
    } finally {
      setIsEditDialogOpen(false);
      setEditingCapability(null);
      setDuplicatingCapability(null);
    }
  };

  const handleSaveCapability = async (updatedCapability: WorkerTool) => {
    const isNew = updatedCapability.id.startsWith("new-");

    // The user did not specify how to handle creation, so we only handle updates.
    if (isNew) {
      setCapabilities((prev) => [...prev, updatedCapability]);
      setIsEditDialogOpen(false);
      setEditingCapability(null);
      setDuplicatingCapability(null);
      return;
    }

    try {
      const savedCapability = await WorkerAniAPI.updateWorkerTool(
        updatedCapability.id,
        updatedCapability
      );
      setCapabilities((prev) =>
        prev.map((c) => (c.id === savedCapability.id ? savedCapability : c))
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to save capability"
      );
    } finally {
      setIsEditDialogOpen(false);
      setEditingCapability(null);
      setDuplicatingCapability(null);
    }
  };

  const capabilitiesByCategory = capabilities.reduce((acc, cap) => {
    const category = cap.tool_type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(cap);
    return acc;
  }, {} as Record<string, WorkerTool[]>);

  if (loading)
    return (
      <div className="py-8 text-center text-slate-500">
        Loading capabilities...
      </div>
    );
  if (error)
    return <div className="py-8 text-center text-red-500">{error}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">
          Worker Capabilities Directory
        </h2>
        <Button onClick={handleCreateCapability}>
          <FontAwesomeIcon icon={faPlus} className="" />
          Create
        </Button>
      </div>
      <p className="text-slate-500 dark:text-slate-400">
        Enhance your worker with specialized superpowers from our capability
        library.
      </p>

      {Object.entries(capabilitiesByCategory).map(
        ([category, capabilities]) => (
          <div key={category} className="mt-8">
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-4">
              {category}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {capabilities.map((cap) => (
                <CapabilityCard
                  key={cap.id}
                  capability={cap}
                  selected={workerData.selectedCapabilities.has(cap.id)}
                  onToggle={toggleCapability}
                  onEdit={handleEditCapability}
                  onDelete={handleDeleteCapability}
                  onDuplicate={handleDuplicateCapability}
                />
              ))}
            </div>
          </div>
        )
      )}

      {editingCapability && (
        <CapabilityEditDialog
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          capability={editingCapability}
          onSave={handleSaveCapability}
        />
      )}

      {duplicatingCapability && (
        <CapabilityEditDialog
          isOpen={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setDuplicatingCapability(null);
          }}
          capability={duplicatingCapability}
          onSave={handleSaveDuplicate}
        />
      )}
    </div>
  );
}
